/**
 * ASSIST-05 — the Purchase Room's strings (brief R14: one file per surface).
 *
 * Source order: the brief's section 6.5 and 6.10 wording wins, then the board
 * (docs/assist/design/boards/Main.dc.html for both layouts,
 * RoomMobile.dc.html where the phone board words a line more briefly). The
 * board's sample data (names, dates, amounts, “Kileleshwa”) is never here:
 * every value arrives as an argument, from get_purchase_room.
 *
 * Strings marked NEW are not drawn on either board: the data has a state the
 * boards do not show (a stop reason other than the name mismatch, a case that
 * has ended, an event in the activity list). They follow the board's voice and
 * are listed in the ASSIST-05 PR for the Brain and the advocate.
 *
 * Imports nothing, so `tsx` tests load it directly. Typographic apostrophes.
 */

type Party =
  | 'buyer'
  | 'advisor'
  | 'seller'
  | 'sellers_agent'
  | 'advocate'
  | 'management_company'
  | 'valuer'
  | 'surveyor'
  | 'lands'
  | 'bank';

const NUMBER_WORDS = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];

/** “A”, “A and B”, “A, B and C” (the board's list style, no serial comma). */
export function joinList(items: readonly string[]): string {
  if (items.length <= 1) return items[0] ?? '';
  return `${items.slice(0, -1).join(', ')} and ${items[items.length - 1]}`;
}

const capitalise = (s: string) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

export const roomCopy = {
  /** 6.10 item 7: surfaces rendered from `test` config values (R13). */
  testRibbon: 'Test values',
  /** The skeleton's accessible name while the room loads. NEW. */
  loading: 'Loading your purchase',

  chapters: ['Start', 'View', 'Offer', 'Verify', 'Agree', 'Transfer', 'Keys'] as const,

  greeting: {
    /** Main: “Karibu, Amina. Day 19 of your purchase, updated today at 09:12.” (R28) */
    web: (name: string | null, day: number, updated: string) =>
      `Karibu${name ? `, ${name}` : ''}. Day ${day} of your purchase, updated ${updated}.`,
    /** RoomMobile: “Karibu, Amina. Day 19, updated 09:12.” */
    phone: (name: string | null, day: number, updated: string) =>
      `Karibu${name ? `, ${name}` : ''}. Day ${day}, updated ${updated}.`,
    todayAt: (time: string) => `today at ${time}`,
    /** NEW: the last update was before today. */
    onDateAt: (date: string, time: string) => `${date} at ${time}`,
  },

  headline: {
    underWay: 'Checks are under way.',
    waitingOnYou: (n: number) =>
      n <= 1 ? 'One thing is waiting on you.' : `${NUMBER_WORDS[n] ?? n} things are waiting on you.`,
    nothingOnYou: 'Nothing is waiting on you.',
    paused: 'We paused your purchase.',
    /** 6.10 item 1: only when no purchase payment has `paid_kes`. */
    noMoneyMoved: 'No money has moved.',
    doNotPay: 'Do not pay anything until this is cleared.',
    /** NEW: cases that are no longer open. */
    withdrawn: 'This purchase was withdrawn.',
    completed: 'Your purchase is complete.',
    closed: 'This purchase is closed.',
    endedFollow: 'Nothing more happens here.',
    completedFollow: 'The title is registered in your name.',
  },

  next: {
    yourMove: 'Your move',
    due: (date: string) => `Due ${date}`,
    /** NEW: the documents the advisor requested for this step. */
    askedFor: (advisor: string, labels: string) => `${advisor} asked you for: ${labels}.`,
    /** Main: “Wanjiku reviews it within [response window].” */
    reviewsWithin: (advisor: string, window: string) => `${advisor} reviews it within ${window}.`,
    /** Main: “Private to you, Wanjiku and your advocate; staff open files only with a recorded reason.” */
    privateLine: (advisor: string) =>
      `Private to you, ${advisor} and your advocate; staff open files only with a recorded reason.`,
    nothingNeeded: 'Nothing needed from you',
    upToDate: 'You are up to date',
    /** Main: “The seller, the management company and the valuer each have a step. We tell you here the moment one of them moves.” */
    othersBody: (parties: readonly string[]) =>
      parties.length === 0
        ? 'Your advisor is preparing the next step. We tell you here the moment it moves.' // NEW
        : parties.length === 1
          ? `${capitalise(parties[0])} has a step. We tell you here the moment it moves.` // NEW (one party)
          : `${capitalise(joinList(parties))} each have a step. We tell you here the moment one of them moves.`,
    whatHappensNow: 'What happens now',
    /** Main: “If the registry clears the mismatch, the purchase continues. If not, you can withdraw. …” */
    whatHappensBody: (clearsIf: string) =>
      `${clearsIf} If not, you can withdraw. You owe Afriqahome nothing, and your advocate explains what happens to any money already paid.`,
    nothingPaidYet: 'Nothing has been paid, and nothing will be until this is cleared.',
    doNotPayYet: 'Do not pay anything until this is cleared.',
  },

  /**
   * The stopped room's callout per 6.4 hard stop (`stop_case` reasons).
   * `owner_name_mismatch` is the board's; the others are NEW.
   */
  stopReasons: {
    owner_name_mismatch: {
      title: 'The names do not match',
      body: 'The name on the title does not match the seller’s ID. Your advocate is checking it with the registry.',
      clearsIf: 'If the registry clears the mismatch, the purchase continues.',
    },
    encumbrance_without_discharge: {
      title: 'The title has a charge, caveat or court order',
      body: 'The search shows a charge, caveat or court order on the title, with no plan to discharge it. Your advocate is asking the seller’s side how it will be cleared.',
      clearsIf: 'If the seller’s side shows how it will be discharged, the purchase continues.',
    },
    search_consent_refused: {
      title: 'The seller refused the official search',
      body: 'The registered owner has not approved the official search on Ardhisasa. We do not continue without it.',
      clearsIf: 'If the owner approves the search, the purchase continues.',
    },
    payment_before_verify: {
      title: 'Someone asked for money too early',
      body: 'You were asked to pay before your advocate’s checks are done. No deposit is paid until you say Go, after your advocate’s report.',
      clearsIf: 'If this is cleared, the purchase continues.',
    },
    personal_account_payee: {
      title: 'A personal account was given for payment',
      body: 'Someone asked for payment to a personal account. Purchase money goes only to your advocate’s client account, a bank or the government.',
      clearsIf: 'If this is cleared, the purchase continues.',
    },
    payment_details_changed: {
      title: 'Payment details changed outside this room',
      body: 'Payment details reached you outside this room. Payment details only change inside this room, and are confirmed by phone with your advocate’s firm.',
      clearsIf: 'If this is cleared, the purchase continues.',
    },
    subdivision_without_mutation: {
      title: 'The subdivision is not registered',
      body: 'The plot was subdivided, but no mutation is registered. Your advocate is checking it with the registry.',
      clearsIf: 'If the registry confirms the mutation, the purchase continues.',
    },
    other: {
      title: 'Your advisor paused this purchase',
      body: 'Your advisor found something that has to be cleared before anything else happens.',
      clearsIf: 'If this is cleared, the purchase continues.',
    },
  },

  /** Inline (“waiting on the seller”) and label (“Seller”) forms of 6.2 `waiting_on`. */
  partyInline: (party: Party, advisor: string): string =>
    ({
      buyer: 'you',
      advisor,
      seller: 'the seller',
      sellers_agent: 'the seller’s agent',
      advocate: 'your advocate',
      management_company: 'the management company',
      valuer: 'the valuer',
      surveyor: 'the surveyor',
      lands: 'the land registry',
      bank: 'the bank',
    })[party],
  partyLabel: (party: Party, advisor: string): string =>
    ({
      buyer: 'You',
      advisor,
      seller: 'Seller',
      sellers_agent: 'Seller’s agent',
      advocate: 'Your advocate',
      management_company: 'Management company',
      valuer: 'Valuer',
      surveyor: 'Surveyor',
      lands: 'Land registry',
      bank: 'Bank',
    })[party],
  /** When no advisor is assigned (an admin is reassigning the case). NEW. */
  advisorFallback: 'your advisor',

  waiting: {
    title: 'Waiting on others',
    since: (date: string) => `Since ${date}`,
  },

  journey: {
    title: 'Your purchase in 7 chapters',
    listLabel: 'Purchase chapters',
    /** Main: “Verify usually takes 1 to 3 weeks. It started on 6 Oct.” */
    verifyTakes: 'Verify usually takes 1 to 3 weeks.',
    itStarted: (date: string) => `It started on ${date}.`,
    /** NEW: the same line for the other chapters. */
    chapterStarted: (chapter: string, date: string) => `${chapter} started on ${date}.`,
    agreed: (date: string) => `Agreed ${date}`,
    checks: (done: number, total: number) => `${done} of ${total} checks`,
    /** Under a future chapter. Agree, Transfer and Keys are the board's; the others are NEW. */
    future: ['Your request', 'Viewing', 'Your offer', 'Your advocate’s checks', 'After your Go', 'Title in your name', 'Handover'] as const,
    done: 'Done',
    noDeposit: 'No deposit is paid until you say Go, after your advocate’s report.',
    noDepositShort: 'No deposit is paid until you say Go.',
    phoneChapter: (n: number, name: string) => `Chapter ${n} of 7: ${name}`,
    /** RoomMobile: “Done: Start, View and Offer. Next: Agree, then Transfer and Keys.” */
    phoneSummary: (done: readonly string[], next: readonly string[]) => {
      const parts: string[] = [];
      if (done.length) parts.push(`Done: ${joinList(done)}.`);
      if (next.length === 1) parts.push(`Next: ${next[0]}.`);
      else if (next.length > 1) parts.push(`Next: ${next[0]}, then ${joinList(next.slice(1))}.`);
      return parts.join(' ');
    },
    phoneProgressLabel: (current: number) =>
      current <= 1
        ? 'Chapter 1 in progress'
        : current === 2
          ? 'Chapter 1 done, chapter 2 in progress'
          : `Chapters 1 to ${current - 1} done, chapter ${current} in progress`,
  },

  property: {
    agreedPrice: (date: string) => `Agreed price, ${date}`,
    /** NEW: before a price is agreed. */
    askingPrice: 'Asking price',
    belowAsking: (amount: string) => `${amount} below asking`,
    /** RoomMobile: “Agreed 2 Oct, KES 500,000 below asking” */
    agreedBelowAsking: (date: string, amount: string) => `Agreed ${date}, ${amount} below asking`,
    viewListing: 'View listing',
    photoAlt: 'Photo of the home',
    photoCredit: (credit: string) => `Photo: ${credit}`,
  },

  done: {
    title: 'Done for you so far',
    by: (advisor: string) => `By ${advisor}, your purchase advisor`,
    belowAskingBody: (asking: string, advisor: string, agreed: string, date: string) =>
      `Asking was ${asking}. ${advisor} negotiated; you agreed ${agreed} on ${date}.`,
    belowAskingShort: (advisor: string, date: string) => `Negotiated by ${advisor}, agreed ${date}`,
    sellerPays: 'The seller’s side pays our fee',
    sellerPaysBody: (date: string) => `You pay Afriqahome nothing, ${date}`,
    /** NEW: who checked a done step, and when. */
    checkedBy: (name: string, date: string) => `${name}, ${date}`,
  },

  checks: {
    title: 'Checks and evidence',
    intro: 'Every check, who did it and when. Nothing is marked done without evidence.',
    introPhone: 'Who checked what, and when. Nothing is done without evidence.',
    count: (done: number, total: number) => `${done} of ${total} done`,
    done: (name: string, date: string) => `Done. Checked by ${name} on ${date}`,
    donePhone: (name: string, date: string) => `Done by ${name}, ${date}`,
    waiting: (party: string, date: string | null) => (date ? `Waiting on ${party} since ${date}` : `Waiting on ${party}`),
    inProgress: (party: string) => `In progress with ${party}`,
    booked: (when: string) => `Booked for ${when}`,
    notStarted: 'Not started',
    stopped: 'Stopped',
    openEvidence: (label: string) => `Open ${label}`,
  },

  /** The line under a step, by `step_key`. Board strings, plus 6.10 item 2, section 2 and R12. */
  stepNotes: {
    representation_letter: 'A free letter that says we work only for you.',
    advocate_engaged: 'You instruct and pay your own independent advocate.',
    official_search: 'The registered owner must approve the search in their own Ardhisasa account.',
    encumbrances: 'Read from the official search result.',
    rates_land_rent: 'County rates and land rent.',
    service_charge: 'Statement and arrears check.',
    valuation: 'Registered valuer, site visit.',
    advocate_report_go: 'You decide Go or No-go after the report. No deposit is paid until you say Go.',
    own_ardhisasa: 'Afriqahome never asks for your Ardhisasa password or OTP.',
  } as Record<string, string>,

  fee: {
    title: 'Who pays us',
    /** 6.5 Asking. */
    askingHeadline: 'We are asking the seller’s agent to pay us.',
    askingBody: (agency: string) =>
      `If ${agency} agrees, you pay us nothing. If they refuse, you decide whether to sign our buyer agreement. Nothing is owed until then.`,
    /** 6.5 Seller’s side pays. */
    sellerHeadline: 'You pay Afriqahome nothing.',
    sellerBody: (agency: string | null, share: string, advisor: string) =>
      `${agency ? `${agency}, the seller’s agent,` : 'The seller’s agent'} agreed to share their commission with us: ${share}% of the price, paid at completion. ${capitalise(advisor)} still works only for you.`,
    /** 6.5 Buyer agreement; 6.10 item 3 when a floor is set. */
    buyerHeadline: (rate: string, floor: string | null) =>
      floor ? `Our fee: ${rate}% of the final price, at least ${floor}.` : `Our fee: ${rate}% of the final price.`,
    /** NEW: the rate is not set in the config row yet. */
    buyerHeadlineNoRate: 'Our fee is set in our buyer agreement.',
    buyerBodySigned: (agency: string, date: string) =>
      `${agency} declined to share their commission, so you signed our buyer agreement on ${date}. Nothing is due until the title is registered in your name.`,
    /** NEW: the no-answer and not-yet-signed variants of the line above. */
    buyerBodyNoAnswerSigned: (agency: string, date: string) =>
      `${agency} did not answer in time, so you signed our buyer agreement on ${date}. Nothing is due until the title is registered in your name.`,
    buyerBodyOffered: (agency: string) =>
      `${agency} declined to share their commission. You decide whether to sign our buyer agreement; nothing is owed unless you do.`,
    buyerBodyNoAnswerOffered: (agency: string) =>
      `${agency} did not answer in time. You decide whether to sign our buyer agreement; nothing is owed unless you do.`,
    settledLabel: 'How the fee was settled',
    pendingLabel: 'How the fee is being settled',
    asked: (agency: string) => `We asked ${agency} to share their commission`,
    agreed: (agency: string, share: string) => `${agency} agreed: ${share}% of the price`,
    saw: 'You saw the amount, here in your room',
    declined: (agency: string) => `${agency} declined`,
    /** 6.5: “<Agency> did not answer by <date>”. */
    noAnswer: (agency: string, date: string) => `${agency} did not answer by ${date}`,
    signed: 'You signed our buyer agreement',
    waitingOnAgent: 'Waiting on the seller’s agent',
    answerDue: (date: string) => `Answer due ${date}`,
    choice: 'Your choice: sign or stop',
    onlyIfRefuse: 'Only if they refuse',
    cap: (cap: string) => `Our total pay on this purchase is capped at ${cap}% of the price, whoever pays it.`,
    /** In a sentence when the agency's name is not known. */
    sellersAgent: 'the seller’s agent',
  },

  money: {
    title: 'Where your money goes',
    intro: 'Afriqahome never holds your purchase money. Pay only accounts marked Payee checked.',
    introPhone: 'Afriqahome never holds your purchase money.',
    kinds: {
      deposit: 'Deposit',
      balance: 'Balance',
      stamp_duty: 'Stamp duty',
      advocate_fee: 'Advocate’s fee', // NEW
      other: 'Other payment', // NEW
    } as Record<string, string>,
    to: (payee: string, clientAccount: boolean) => `To ${payee}${clientAccount ? ', client account' : ''}`,
    payeeChecked: (date: string) => `Payee checked, ${date}`,
    payeeCheckedPhone: 'Payee checked. Pay only after your Go.',
    /** 6.10 placeholder “[last 4]”. */
    accountEnding: (last4: string) => `Account ending ${last4}`,
    /** AC-05. */
    unverified: 'Unverified, do not pay',
    payAfterGo: 'Pay only after your Go',
    stampDutyNote: 'An estimate, paid to the government on Ardhipay.',
    balanceNote: 'Through your advocate or bank at completion. Held until the transfer registers.',
    balanceNotePhone: 'Through your advocate or bank at completion.',
    paid: (date: string) => `Paid ${date}`, // NEW
    feeLabel: 'Afriqahome fee',
    feeLabelRate: (rate: string) => `Afriqahome fee, ${rate}%`,
    fromYou: (zero: string) => `${zero} from you`,
    sellerNote: (cap: string | null) =>
      `Paid by the seller’s side from their commission, at completion.${cap ? ` Capped at ${cap}% of the price.` : ''}`,
    sellerNotePhone: (cap: string | null) =>
      `Paid by the seller’s side from their commission.${cap ? ` Capped at ${cap}% of the price.` : ''}`,
    notDueYet: 'Not due yet',
    buyerNote: 'Due when the title is registered in your name. Invoiced by Afriqahome, separate from the purchase money.',
    nothingYet: 'Nothing yet',
    askingNote: 'Waiting on the seller’s agent. Nothing is due unless you sign our buyer agreement.',
    offeredNote: 'Nothing is due unless you sign our buyer agreement.', // NEW
    invoiced: 'Invoiced', // NEW
    warnTitle: 'New payment details? Do not pay.',
    /** Main ends the first sentence with “here”; the thread arrives in ASSIST-06. */
    warnBody: (advisor: string) =>
      `Tell ${advisor}. Payment details only change inside this room, and ${advisor} confirms them by phone with your advocate’s firm.`,
  },

  team: {
    title: 'Your team',
    advisorRole: 'Purchase advisor, works only for you',
    earb: (no: string, year: number | null) => (year ? `EARB reg. ${no}, practising ${year}` : `EARB reg. ${no}`),
    advocateRole: 'Your advocate, paid by you',
    sellersAgent: 'Seller’s agent',
    sellersAgentRole: 'Represents the seller',
    /** Main: “Verified agent, contact through Wanjiku”; “Verified agent” needs the badge (R9), which the room does not read. */
    contactThrough: (advisor: string) => `Contact through ${advisor}`,
    sellersAgentRolePhone: (advisor: string) => `Represents the seller, via ${advisor}`,
    valuerRole: 'Registered valuer',
    siteVisit: (date: string) => `Site visit ${date}`,
  },

  documents: {
    title: 'Documents',
    intro: (advisor: string) =>
      `Private to you, ${advisor} and your advocate. Afriqahome staff open a file only with a recorded reason. Every view is logged.`,
    groups: {
      identity: 'Identity',
      property: 'Property',
      legal: 'Legal',
      payment: 'Payments', // NEW
      other: 'Other', // NEW
    } as Record<string, string>,
    accepted: (date: string) => `Accepted ${date}`,
    uploaded: (date: string) => `Uploaded ${date}`, // NEW
    rejected: (reason: string | null) => (reason ? `Not accepted: ${reason}` : 'Not accepted'), // NEW
    requested: (date: string) => `Requested ${date}`,
    fromYou: (date: string | null) => (date ? `From you, due ${date}` : 'From you'),
    open: 'Open',
    openLabel: (label: string) => `Open ${label}`,
    openFailed: 'We couldn’t open the document. Try again.', // NEW
    none: 'No documents yet.', // NEW
  },

  /** NEW: the room's activity list (buyer-visible `case_events`). */
  activity: {
    title: 'Activity',
    case_created: 'You asked for a purchase advisor',
    case_withdrawn: 'The purchase was withdrawn',
    step: (title: string, status: string) => `${title}: ${status}`,
    stepStatus: {
      not_started: 'not started',
      in_progress: 'in progress',
      waiting: 'waiting',
      done: 'done',
      stopped: 'stopped',
    } as Record<string, string>,
    document_requested: (label: string) => `Document requested: ${label}`,
    document_accepted: (label: string) => `Document accepted: ${label}`,
    document_rejected: (label: string) => `Document not accepted: ${label}`,
    document_uploaded: (label: string) => `Document uploaded: ${label}`,
    representation_letter_signed: 'You signed the representation letter',
    buyer_agreement_signed: 'You signed our buyer agreement',
    payee_recorded: (payee: string) => `Payee recorded: ${payee}`,
    payment_updated: (kind: string) => `Payment updated: ${kind}`,
    payee_verified: (payee: string) => `Payee checked by phone: ${payee}`,
    payee_changed: (payee: string) => `Payment details changed: ${payee}. Unverified, do not pay`,
    case_stopped: 'Your advisor paused the purchase',
    stop_cleared: 'The pause was cleared',
    agreed_price_set: (price: string) => `Agreed price: ${price}`,
    registered_price_set: (price: string) => `Registered price: ${price}`,
    reminder_recorded: (title: string) => `Reminder: ${title}`,
    chapter_advanced: (chapter: string) => `${chapter} started`,
    case_reassigned: (advisor: string) => `Your advisor is now ${advisor}`,
    case_reassigned_plain: 'Your advisor changed',
    fee_invoiced: 'Afriqahome fee invoiced',
    fee_paid: 'Afriqahome fee paid',
    go: 'You said Go',
    noGo: 'You said No-go',
  },

  /** R12. RoomMobile's wording; Main adds “If anyone does, report it here.”, which needs the thread (ASSIST-06). */
  ardhisasa: 'Afriqahome never asks for your Ardhisasa password or OTP.',

  nav: {
    myPurchase: 'My purchase',
    /** NEW: the account row's subtitle when the case has no listing title. */
    untitled: 'Your Purchase Room',
  },
} as const;
